import React, { useEffect, useState } from 'react';
import { Alert, Icon, Box, ProgressBar, SpaceBetween } from '@amzn/awsui-components-core';

/** Represents a single phase in the scanning progress animation. */
interface ScanningPhase {
  label: string;
  progress: number;
}

/**
 * Ordered list of phases displayed during the duplicate check.
 * Each phase shows a descriptive label and a progress percentage.
 * These are cosmetic — they don't reflect actual backend progress,
 * but give the user a sense of forward motion while the scan runs.
 */
const SCANNING_PHASES: ScanningPhase[] = [
  { label: 'Scanning AWS service documentation', progress: 10 },
  { label: 'Scanning AWS Blogs', progress: 30 },
  { label: 'Scanning AWS Getting Started', progress: 50 },
  { label: 'Scanning APG Library patterns', progress: 70 },
  { label: 'Scanning APG Library guides', progress: 90 },
];

/** Time in ms between each phase transition. */
const PHASE_INTERVAL_MS = 10000;

/** "Did you know?" footer content, exported for use as a Container footer. */
export const DuplicateCheckFooter: React.FC = () => (
  <Box padding={{ vertical: 'xs' }}>
    <SpaceBetween size="m" direction="vertical">
      <SpaceBetween size="s" direction="horizontal">
        <Box>
          <Icon url="/images/lightbulb.png" variant="normal" size="large" />
        </Box>
        <Box fontSize="heading-m" padding={{ top: 'xxs' }}>
          <Box fontWeight="bold">Did you know?</Box>
          <Box color="text-body-secondary">
            This automated duplication scan replaces a manual review, and it eliminates a seven-day
            SLA.
          </Box>
        </Box>
      </SpaceBetween>
      <Alert type="info">
        This feature uses generative AI. Results may be inaccurate or incomplete and should be
        verified against authoritative sources.
      </Alert>
    </SpaceBetween>
  </Box>
);

/**
 * Animated loading screen shown while the duplicate content check
 * is running on the backend. Cycles through `SCANNING_PHASES` on a
 * fixed interval to simulate progress.
 */
const DuplicateCheckLoadingScreen: React.FC = () => {
  const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);

  // Advance through scanning phases on a timer, stopping at the last one.
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhaseIndex((prev) => {
        if (prev >= SCANNING_PHASES.length - 1) {
          window.clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, PHASE_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, []);

  const currentPhase = SCANNING_PHASES[currentPhaseIndex];

  return (
    <div className="duplicate-check-progress-bar">
      <ProgressBar
        // @ts-expect-error TS18048 - strict migration
        value={currentPhase.progress}
        label="Duplicate content scan in progress (May take a minute)"
        // @ts-expect-error TS18048 - strict migration
        additionalInfo={currentPhase.label}
      />
    </div>
  );
};

export default DuplicateCheckLoadingScreen;
